import { useEffect, useState } from 'react';
import styles from './HomePage.module.css'
import { useAppContext } from '../context/AppContext';
import SalesBarchart from '../components/charts/SalesBarChart';
import { MessageThreadModel } from '../models/MessageThreadModel';
import messageService from '../services/messageService';
import { formatDate } from '../utils/dateUtils';
import { Link, useNavigate } from 'react-router-dom';
import { OfferModel } from '../models/OfferModel';
import offerService from '../services/offerService';
import { formatPrice } from '../utils/currencyUtils';
import OfferTermsComp from '../components/OfferTermsComp';
import itemService from '../services/itemService';
import noPostalCodeCountries from '../data/noPostalCodeCountries.json';

const HomePage = () => {
  const navigate = useNavigate();
  const itemImageURL = process.env.REACT_APP_ITEM_IMAGE_URL;
  const { seller, getAccountFullName } = useAppContext();
  const [messageThreads, setMessageThreads] = useState<MessageThreadModel[]>([]);
  const [offers, setOffers] = useState<OfferModel[]>([]);
  const [isMessageLoading, setIsMessageLoading] = useState(false);
  const [serviceError, setServiceError] = useState({ message: '', offer: '' });

  const [isOfferLoading, setIsOfferLoading] = useState(false);
  const [isCheckingItemListing, setIsCheckingItemListing] = useState(false);
  const [hasItemListing, setHasItemListing] = useState(false);

  const isBlank = (prop?: string | null) => !prop || prop.trim() === "";

  const isStoreProfileIncomplete = !!seller && (
    isBlank(seller.sellerName) ||
    isBlank(seller.sellerDescription) ||
    isBlank(seller.logo) ||
    isBlank(seller.addressLine1) ||
    isBlank(seller.city) ||
    isBlank(seller.state) ||
    isBlank(seller.country) ||
    // postcode optional for countries without postal codes:
    (!noPostalCodeCountries.includes(seller.country) && isBlank(seller.postcode)) ||
    isBlank(seller.countryCode) ||
    isBlank(seller.email) ||
    isBlank(seller.contactNumber) ||
    isBlank(seller.businessName)
  );


  useEffect(() => {
    const loadThreads = async () => {

      try {

        if (!seller?.sellerId) return;
        const response = await messageService.getMessageThreads(seller.sellerId, -1, 0, 3);
        setMessageThreads(response);

      } catch (error) {
        setServiceError(prev => ({ ...prev, message: 'Failed to load message threads. Please try again later.' }));
      }
      finally {
        setIsMessageLoading(false);
      }
    };

    setIsMessageLoading(true);
    loadThreads();
  }, [seller?.sellerId]);

  useEffect(() => {
    const hasItemListing = async () => {
      try {
        if (!seller?.sellerId) return;
        const hasListing = await itemService.sellerHasItemListing();
        setHasItemListing(hasListing);
      }
      catch (error) {
        setServiceError(prev => ({ ...prev, offer: 'Failed to check item listing. Please try again later.' }));
      }
      finally {
        setIsCheckingItemListing(false);
      }
    }

    setIsCheckingItemListing(true);
    hasItemListing();
  }, []);

  useEffect(() => {
    const loadOffers = async () => {
      try {
        const response: { offers: OfferModel[], offerCount: number } = await offerService.searchOffers(0, 3, '');
        setOffers(response.offers);

      } catch (error) {
        setServiceError(prev => ({ ...prev, offer: 'Failed to load data. Please try again later.' }));

      } finally {
        setIsOfferLoading(false);
      }
    }

    setIsOfferLoading(true);
    loadOffers();

  }, []);

  const updateTermsAccepted = async (offer: OfferModel) => {
    try {
      const result = await offerService.updateOfferTermsAccepted(offer.offerId);
      if (result === "Success") {
        setOffers(prevOffers => prevOffers.map(o => o.offerId === offer.offerId ? { ...o, termsAccepted: "Yes" } : o));
      }
    } catch (error: any) {
      setServiceError(prev => ({
        ...prev,
        offer: error?.message || 'Failed to accept offer terms. Please try again later.'
      }));
    }
  }

  return (
    <div className='my-3'>
      <p className={`ms-1 mb-3 ${styles.welcomeMessage}`}>Hello <span className="fst-italic">{getAccountFullName()} </span>! Here’s what’s happening at <strong>{seller?.sellerName}</strong>.</p>

      {/* Alert 1: No items */}
      {!isCheckingItemListing && !hasItemListing && (
        <div className="alert alert-warning rounded-0 mb-2 py-2" role="alert">
          No products have been added yet. Please go to the{" "}
          <Link to="/items" className="alert-link">Items</Link>{" "}
          menu to create your first product.
        </div>
      )}

      {/* Alert 2: Store settings incomplete */}
      {isStoreProfileIncomplete && (
        <div className="alert alert-warning rounded-0 mb-2 py-2" role="alert">
          Your store profile is incomplete. Please go to{" "}
          <Link to="/storeSettings" className="alert-link">Store Settings</Link>{" "}
          to update your store information.
        </div>
      )}
      <div className='row d-flex align-items-stretch'>
        <div className='col-md-6 d-flex'>
          <div className="card rounded-0 mt-2 flex-grow-1">
            <div className="card-body  p-4">
              <h5 className="card-title mb-3">Last 6 Months Sale</h5>
              <SalesBarchart />
            </div>
          </div>
        </div>
        <div className='col-md-6 d-flex'>
          <div className="card rounded-0 mt-2 flex-grow-1">
            <div className="card-body p-4 ">
              <div className="card-title mb-3 d-flex">
                <h5 className='flex-grow-1'>Messages</h5>
                {messageThreads.length > 0 && (
                  <Link to="/messages" className="text-decoration-none float-end" style={{ color: '#d5393d', cursor: 'pointer' }}>View All</Link>
                )}
              </div>
              {serviceError.message && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError.message}</div></div>}
              {isMessageLoading && (<div className="text-center gap-2 mt-3 text-secondary">
                <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                <span>  Loading...</span>
              </div>)}
              {!isMessageLoading && messageThreads.length === 0 ? (
                <div className="d-flex flex-column justify-content-center align-items-center text-muted" style={{ height: '150px' }}>
                  <i className="bi bi-chat-dots fs-2 mb-2"></i>
                  <span>No messages requiring attention.</span>
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {!isMessageLoading && messageThreads.map((thread, index) => (
                    <div className="w-100" key={thread.messageThreadId} onClick={() => navigate(`/messages/${thread.messageThreadId}`)}>
                      <div className={`d-flex align-items-center ${index < messageThreads.length - 1 ? "border-bottom" : ""} w-100 hover-bg-light`} key={thread.messageThreadId}>
                        <div className={`p-2 flex-grow-1 ${thread.sellerUnread ? 'fw-bold' : 'fw-normal'}`} style={{ cursor: 'pointer', maxWidth: '100%', minWidth: 0 }}>
                          <div className={`text-truncate`}>{thread.subject}</div>
                          <div className="text-muted small text-truncate" style={{ maxWidth: '100%' }}>
                            {thread.lastMessage?.senderRole === "Seller" ? (
                              "You replied: "
                            ) : (
                              <i className="bi bi-chat me-1"></i>
                            )}
                            {thread.lastMessage?.messageContent || ''}
                          </div>
                          <div className="text-muted small mt-1 mb-1">
                            {formatDate(thread.lastUpdatedTime)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='col-md-12'>
          <div className="card rounded-0 mt-4 mb-5">
            <div className="card-body  p-4">
              <div className="card-title mb-3 d-flex">
                <h5 className='flex-grow-1'>Offers</h5>
                {offers.length > 0 && (
                  <Link to="/offers" className="text-decoration-none float-end" style={{ color: '#d5393d', cursor: 'pointer' }}>View All</Link>
                )}
              </div>
              {serviceError.offer && <div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceError.offer}</div></div>}
              {isOfferLoading && (<div className="text-center gap-2 mt-3 text-secondary">
                <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                <span>  Loading...</span>
              </div>)}
              {!isOfferLoading && offers.length === 0 ? (
                <div className="d-flex flex-column justify-content-center align-items-center text-muted" style={{ height: '150px' }}>
                  <i className="bi bi-bag fs-2 mb-2"></i>
                  <span>No offers available.</span>
                </div>
              ) : (
                offers.map((offer, index) => (
                  <div className={` pt-3 hover-bg-light ${index < offers.length - 1 ? 'border-bottom' : 'pb-2'}`} style={{ cursor: 'pointer' }} key={offer.offerId} >
                    <div className={`row d-flex justify-content-space-between px-2`}>
                      <div className='col-md-6'>ID: #{offer.offerId}</div>
                      {offer.termsAccepted != "" && <div className={`col-md-6 ${styles.offerCreatedItem} text-end`}>{formatDate(offer.createdTime)}</div>}
                    </div>

                    <div className={`align-items-center col-md-12 d-flex flex-row mb-4 mt-2 px-2`}>
                      {offer.termsAccepted === "" ?
                        (
                          <OfferTermsComp
                            offer={offer}
                            onAccept={updateTermsAccepted}
                          />

                        ) :
                        (
                          <div className={`d-flex flex-row flex-grow-1 text-muted small`} onClick={() => navigate(`/offers/view/${offer.offerId}`, { state: { offer: offer } })}>
                            <img className={`float-start border ${styles.offerItemImage}`} src={offer.itemImage ? `${itemImageURL}/${offer.itemImage}` : "/images/fallback.png"}></img>
                            <div className='flex-grow-1 ms-4'>
                              <p className='one-line-text-ellipsis mt-1 mb-2'>{offer.itemName}</p>
                              <div className='row'>
                                <div className={`col-md-2 float-start ${styles.offerPrice}`}>Price: {formatPrice(offer.itemPrice)}</div>
                                <div className={`col-md-3 float-start ${styles.offerQty}`}>Quantity: {offer.quantity}</div>
                                <div className={`col-md-7 text-end ${styles.offerAmount}`}>Amount: {formatPrice(offer.amount)}</div>
                              </div>
                            </div>
                          </div>)}

                    </div>
                  </div>
                ))
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
